import { For, Show, type JSX } from 'solid-js'
import type { ViewProps } from 'wdc-cube-solid'
import type { CompanyConfirmScope } from 'mormaii-web-presentation/company-confirm'

import { Modal } from '../../../../components/dialog'
import { Button } from '../../../../components/filters'

import Css from './company-confirm.module.scss'

/**
 * A confirmacao de bloquear, liberar ou remover uma empresa.
 *
 * Mostra a empresa INTEIRA antes de perguntar: quem vai bloquear um acesso confere o
 * CNPJ e o contato na propria pergunta, e nao na tela de tras. Numa lista de dez
 * linhas parecidas, e isso que separa bloquear a empresa certa de bloquear a vizinha.
 */
export function CompanyConfirmView(props: ViewProps<CompanyConfirmScope>): JSX.Element {
    const remove = () => props.scope.kind === 'delete'
    const unblock = () => props.scope.kind === 'unblock'

    const fields = () => [
        ['CNPJ', props.scope.cnpj],
        ['Razão social', props.scope.razaoSocial],
        ['Nome fantasia', props.scope.nomeFantasia],
        ['Ramo de atividade', props.scope.ramoAtividade],
        ['Contato', props.scope.contactName],
        ['Telefone', props.scope.contactPhone],
        ['E-mail', props.scope.contactEmail]
    ]

    return (
        <Modal
            wide
            title={props.scope.title}
            onClose={() => void props.scope.onClose()}
            footer={
                <>
                    <Button variant="ghost" onClick={() => void props.scope.onClose()}>
                        Cancelar
                    </Button>
                    <Button
                        variant={remove() ? 'danger' : 'primary'}
                        disabled={props.scope.busy}
                        onClick={() => void props.scope.onConfirm()}
                    >
                        {props.scope.busy ? 'Aplicando…' : props.scope.confirmLabel}
                    </Button>
                </>
            }
        >
            <div class={Css.body}>
                <div class={Css.question}>
                    <span class={Css.symbol} classList={{ [Css.danger]: remove(), [Css.release]: unblock() }}>
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="1.75"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        >
                            {remove() ? (
                                <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6" />
                            ) : unblock() ? (
                                <path d="M7 11V7a5 5 0 0 1 9.9-1M5 11h14v10H5z" />
                            ) : (
                                <path d="M7 11V7a5 5 0 0 1 10 0v4M5 11h14v10H5z" />
                            )}
                        </svg>
                    </span>

                    <div class={Css.texts}>
                        <p class={Css.text}>{props.scope.question}</p>
                        <Show when={props.scope.explanation}>
                            <p class={Css.explanation}>{props.scope.explanation}</p>
                        </Show>
                    </div>
                </div>

                <dl class={Css.facts}>
                    <For each={fields()}>
                        {([label, value]) => (
                            <div class={Css.fact}>
                                <dt class={Css.factLabel}>{label}</dt>
                                <dd class={Css.factValue}>{value || '-'}</dd>
                            </div>
                        )}
                    </For>
                </dl>

                <Show when={unblock()}>
                    <section class={Css.attempts}>
                        <h3 class={Css.attemptsTitle}>Tentativas de acesso durante o bloqueio</h3>
                        <Show
                            when={!props.scope.loadingAttempts}
                            fallback={<p class={Css.attemptsNote}>Carregando…</p>}
                        >
                            <Show
                                when={props.scope.attempts.length > 0}
                                fallback={<p class={Css.attemptsNote}>Ninguém tentou entrar no painel.</p>}
                            >
                                <ul class={Css.attemptList}>
                                    <For each={props.scope.attempts}>
                                        {(attempt) => (
                                            <li class={Css.attempt}>
                                                <span class={Css.attemptWhen}>{attempt.when}</span>
                                                <span class={Css.attemptWho}>
                                                    {attempt.name} · {attempt.email}
                                                </span>
                                            </li>
                                        )}
                                    </For>
                                </ul>
                            </Show>
                        </Show>
                    </section>
                </Show>
            </div>
        </Modal>
    )
}
